import React from 'react';
import { Col, Divider, Row } from 'antd';
import { isEqual } from 'lodash';

import {
  ContactPersonModel,
  CustomerHistoryDetailsModel,
  FullCustomerInfoResponseModel,
} from 'api/adminApiClient';
import HistoryLog from 'components/ActivitiesDetailsSection/HistorySection/HistoryList/HistoryCard/HistoryLog';
import HistoryField from 'components/ActivitiesDetailsSection/HistorySection/HistoryList/HistoryCard/HistoryField';

import styles from 'components/ActivitiesDetailsSection/HistorySection/HistoryList/HistoryCard/HistoryCard.module.less';

type Props = {
  historyDetails: CustomerHistoryDetailsModel;
  historyId: string;
};

export default function GeneralInformationDetailsCard(props: Props) {
  const { historyDetails, historyId } = props;
  const { oldData, newData } = historyDetails;
  const hasGeneralInfoSectionChanged = !isEqual(
    oldData?.generalInfo?.generalInfo?.companyInfo,
    newData?.generalInfo?.generalInfo?.companyInfo,
  );

  const hasPrimaryContactsSectionChanged = !isEqual(
    oldData?.generalInfo?.generalInfo?.primaryContacts,
    newData?.generalInfo?.generalInfo?.primaryContacts,
  );

  // General info section checks
  const hasCompanyNameChanged = !isEqual(
    oldData?.generalInfo?.generalInfo?.companyInfo?.companyName,
    newData?.generalInfo?.generalInfo?.companyInfo?.companyName,
  );
  const hasCompanyWebsiteChanged = !isEqual(
    oldData?.generalInfo?.generalInfo?.companyInfo?.companyWebSite,
    newData?.generalInfo?.generalInfo?.companyInfo?.companyWebSite,
  );
  const hasCompanyEmailChanged = !isEqual(
    oldData?.generalInfo?.generalInfo?.companyInfo?.companyEmail,
    newData?.generalInfo?.generalInfo?.companyInfo?.companyEmail,
  );
  const hasCompanyPhoneNumberChanged = !isEqual(
    oldData?.generalInfo?.generalInfo?.companyInfo?.companyPhoneNumber,
    newData?.generalInfo?.generalInfo?.companyInfo?.companyPhoneNumber,
  );
  const hasCompanyCountryChanged = !isEqual(
    oldData?.generalInfo?.generalInfo?.companyInfo?.location?.country,
    newData?.generalInfo?.generalInfo?.companyInfo?.location?.country,
  );
  const hasCompanyStateChanged = !isEqual(
    oldData?.generalInfo?.generalInfo?.companyInfo?.location?.state ||
      oldData?.generalInfo?.generalInfo?.companyInfo?.location
        ?.internationalStateName,
    newData?.generalInfo?.generalInfo?.companyInfo?.location?.state ||
      newData?.generalInfo?.generalInfo?.companyInfo?.location
        ?.internationalStateName,
  );
  const hasCompanyCityChanged = !isEqual(
    oldData?.generalInfo?.generalInfo?.companyInfo?.location?.city ||
      oldData?.generalInfo?.generalInfo?.companyInfo?.location
        ?.internationalCityName,
    newData?.generalInfo?.generalInfo?.companyInfo?.location?.city ||
      newData?.generalInfo?.generalInfo?.companyInfo?.location
        ?.internationalCityName,
  );
  const hasCompanyAddressChanged = !isEqual(
    oldData?.generalInfo?.generalInfo?.companyInfo?.location?.address,
    newData?.generalInfo?.generalInfo?.companyInfo?.location?.address,
  );
  const hasCompanyZipCodeChanged = !isEqual(
    oldData?.generalInfo?.generalInfo?.companyInfo?.location?.zipCode,
    newData?.generalInfo?.generalInfo?.companyInfo?.location?.zipCode,
  );
  const hasAdditionalInvoiceNameChanged = !isEqual(
    oldData?.generalInfo?.generalInfo?.companyInfo?.additionalInvoiceName,
    newData?.generalInfo?.generalInfo?.companyInfo?.additionalInvoiceName,
  );

  const hasAnyCompanyFieldChanged =
    hasCompanyNameChanged ||
    hasCompanyWebsiteChanged ||
    hasCompanyEmailChanged ||
    hasCompanyPhoneNumberChanged ||
    hasCompanyCountryChanged ||
    hasCompanyStateChanged ||
    hasCompanyCityChanged ||
    hasCompanyAddressChanged ||
    hasCompanyZipCodeChanged ||
    hasAdditionalInvoiceNameChanged;

  const hasAnyDisplayableChange =
    (hasGeneralInfoSectionChanged && hasAnyCompanyFieldChanged) ||
    hasPrimaryContactsSectionChanged;

  if (!hasAnyDisplayableChange) {
    return null;
  }

  const renderGeneralInfoSection = (
    customerDetails?: FullCustomerInfoResponseModel,
  ) => {
    const sectionName = 'general-information';

    return (
      <>
        <Row gutter={[10, 10]}>
          <Col xs={24}>
            <div className={styles.cardTitle}>General information</div>
          </Col>

          {hasCompanyNameChanged && (
            <HistoryField
              title="Company name"
              value={
                customerDetails?.generalInfo?.generalInfo?.companyInfo
                  ?.companyName
              }
              dataId={`${sectionName}-company-name-field`}
            />
          )}

          {hasAdditionalInvoiceNameChanged && (
            <HistoryField
              title="Invoice name (Legal/DBA)"
              value={
                customerDetails?.generalInfo?.generalInfo?.companyInfo
                  ?.additionalInvoiceName
              }
            />
          )}

          {hasCompanyWebsiteChanged && (
            <HistoryField
              title="Company website"
              value={
                customerDetails?.generalInfo?.generalInfo?.companyInfo
                  ?.companyWebSite
              }
              dataId={`${sectionName}-company-website-field`}
            />
          )}

          {hasCompanyCountryChanged && (
            <HistoryField
              title="Country"
              value={
                customerDetails?.generalInfo?.generalInfo?.companyInfo?.location
                  ?.country
              }
              dataId={`${sectionName}-country-field`}
            />
          )}

          {hasCompanyStateChanged && (
            <HistoryField
              title="State"
              value={
                customerDetails?.generalInfo?.generalInfo?.companyInfo?.location
                  ?.state ||
                customerDetails?.generalInfo?.generalInfo?.companyInfo?.location
                  ?.internationalStateName
              }
              dataId={`${sectionName}-state-field`}
            />
          )}

          {hasCompanyCityChanged && (
            <HistoryField
              title="City"
              value={
                customerDetails?.generalInfo?.generalInfo?.companyInfo?.location
                  ?.city ||
                customerDetails?.generalInfo?.generalInfo?.companyInfo?.location
                  ?.internationalCityName
              }
              dataId={`${sectionName}-city-field`}
            />
          )}

          {hasCompanyAddressChanged && (
            <HistoryField
              title="Main address"
              value={
                customerDetails?.generalInfo?.generalInfo?.companyInfo?.location
                  ?.address
              }
              dataId={`${sectionName}-main-address-field`}
            />
          )}

          {hasCompanyZipCodeChanged && (
            <HistoryField
              title="ZIP code"
              value={
                customerDetails?.generalInfo?.generalInfo?.companyInfo?.location
                  .zipCode
              }
              dataId={`${sectionName}-ZIP-code-field`}
            />
          )}

          {hasCompanyEmailChanged && (
            <HistoryField
              title="Email"
              value={
                customerDetails?.generalInfo?.generalInfo?.companyInfo
                  ?.companyEmail
              }
              dataId={`${sectionName}-email-field`}
            />
          )}

          {hasCompanyPhoneNumberChanged && (
            <HistoryField
              title="Phone number"
              value={
                customerDetails?.generalInfo?.generalInfo?.companyInfo
                  ?.companyPhoneNumber
              }
              dataId={`${sectionName}-phone-number-field`}
            />
          )}
        </Row>

        <Divider />
      </>
    );
  };

  const renderPrimaryContactSection = (
    customerDetails?: FullCustomerInfoResponseModel,
  ) => {
    const primaryContacts: ContactPersonModel[] = customerDetails?.generalInfo
      ?.generalInfo?.primaryContacts.length
      ? customerDetails.generalInfo.generalInfo.primaryContacts
      : [
          {
            firstName: '',
            lastName: '',
            email: '',
            phoneNumber: '',
            title: '',
          },
        ];
    const sectionName = 'primary-contact';

    return (
      <>
        <Row gutter={[10, 10]}>
          <Col xs={24}>
            <div className={styles.cardTitle}>Primary contact</div>
          </Col>

          <Col xs={24}>
            {primaryContacts.map((primaryContact, index) => (
              <React.Fragment key={`${index}`}>
                <Row gutter={[10, 10]}>
                  <HistoryField
                    title="First name"
                    value={primaryContact?.firstName}
                    dataId={`${sectionName}-first-name-field`}
                  />

                  <HistoryField
                    title="Last name"
                    value={primaryContact?.lastName}
                    dataId={`${sectionName}-last-name-field`}
                  />

                  <HistoryField
                    title="Email"
                    value={primaryContact?.email}
                    dataId={`${sectionName}-email-field`}
                  />

                  <HistoryField
                    title="Phone number"
                    value={primaryContact?.phoneNumber}
                    dataId={`${sectionName}-phone-number-field`}
                  />

                  <HistoryField
                    title="Title"
                    value={primaryContact?.title}
                    dataId={`${sectionName}-title-field`}
                  />
                </Row>

                <Divider />
              </React.Fragment>
            ))}
          </Col>
        </Row>

        <Divider />
      </>
    );
  };

  const renderGeneralInformation = (
    customerDetails?: FullCustomerInfoResponseModel,
  ) => {
    return (
      <>
        {hasGeneralInfoSectionChanged &&
          renderGeneralInfoSection(customerDetails)}

        {hasPrimaryContactsSectionChanged &&
          renderPrimaryContactSection(customerDetails)}
      </>
    );
  };

  return (
    <HistoryLog
      historyId={historyId}
      oldData={renderGeneralInformation(oldData)}
      newData={renderGeneralInformation(newData)}
    />
  );
}
